import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { z } from 'zod'

import { authAPI } from '@/api/new-api'
import { SortOrder } from '@/components/ui/table'
import { PersonDisplay } from '@/models/features/display-column.models'

export const ReservationStatusEnum = z.enum([
  'pending',
  'approved',
  'cancelled',
  'completed',
  'ongoing',
  'cancellation_requested',
])

export type ReservationStatus = z.infer<typeof ReservationStatusEnum>

export type ReservationSortBy = 'date' | 'endDate' | 'netPaid' | 'createdBy' | 'approvedBy'

export interface GetReservationDashboardRequest {
  buildingId?: number
  clientId?: number[]
  skip?: number
  limit?: number
  status?: ReservationStatus[]
  search?: string
  dateStart?: number
  dateEnd?: number
  amenityId?: number[]
  locationId?: number[]
  sortBy?: ReservationSortBy
  sortOrder?: SortOrder
}

export interface ReservationDashboardRow {
  // Note: the BE's per-column person details - only the fields enabled in its settings (EL-15064)
  reservedForDisplay?: PersonDisplay
  createdByDisplay?: PersonDisplay
  approvedByDisplay?: PersonDisplay
  id: number
  status: ReservationStatus
  residentName: string | null
  residentEmail: string | null
  residentUnit: string | null
  residentPhone: string | null
  createdAt: number
  updatedAt: number
  amenityId: number
  amenityName: string
  amenityCheckInTime: number | null
  amenityCheckOutTime: number | null
  guest: number | null
  client: number | null
  date: number
  endDate: number | null
  startTime: number
  endTime: number
  approved: boolean
  cancelled: boolean
  chargeId: string | null
  refunded: boolean
  approvedAt: number | null
  cancelledBy: string | null
  hidden: boolean
  locationId: number | null
  locationName: string | null
  total: number | null
  refundTotal: number | null
  // Note: computed server-side (total - refundTotal) - can still be null when total is null
  // (a free reservation with no charge row at all), so render with a `?? 0` fallback.
  netPaid: number | null
  guestName: string | null
  guestEmail: string | null
  // Note: the "Reserved For" person - the resident's details, or the guest's (name, email,
  // phone) when the booking is a guest's. All null on a time block.
  reservedForRole?: 'Resident' | 'Guest' | null
  reservedForFirstName?: string | null
  reservedForLastName?: string | null
  reservedForEmail?: string | null
  reservedForPhone?: string | null
  reservedForAvatar?: string | null
  reservedForUnit?: string | null
  reservedForBuildingNumber?: string | null
  reservedForStreet?: string | null
  source?: string
  createdByUser?: { firstName: string; lastName: string }
  // Note: the creator is staff or, for a booking made in the app, the resident (role "Client") -
  // only then are the client id, unit, building number and address set
  createdByEmail?: string | null
  createdByAvatar?: string | null
  createdByRole?: string | null
  createdByClientId?: number | null
  createdByUnit?: string | null
  createdByBuildingNumber?: string | null
  createdByStreet?: string | null
  approvedByUser?: { firstName: string; lastName: string }
  approvedByEmail?: string | null
  approvedByAvatar?: string | null
  approvedByRole?: string | null
}

// Note: keyed by amenity/location id; ignores the amenityId/locationId filters, an amenity's
// count includes all its locations.
export interface ReservationDashboardFilterCounts {
  amenities: Record<string, number>
  locations: Record<string, number>
}

export interface GetReservationDashboardResponse {
  count: number
  countPending: number
  countCompleted: number
  countApproved: number
  countOngoing: number
  countCancelled: number
  countCancellationRequested: number
  filterCounts: ReservationDashboardFilterCounts
  results: ReservationDashboardRow[]
}

export const USE_GET_RESERVATION_DASHBOARD_QUERY_KEY = 'useGetReservationDashboard'

export const getReservationDashboardQueryKey = (data: GetReservationDashboardRequest) => [
  USE_GET_RESERVATION_DASHBOARD_QUERY_KEY,
  data,
]

export const getReservationDashboard = async (data: GetReservationDashboardRequest) => {
  const response = await authAPI.get<GetReservationDashboardResponse>('/v2/reservation/dashboard', {
    params: data,
  })
  return response.data
}

// Note: omit dateStart/dateEnd to get every reservation of the building ("all dates").
export const useGetReservationDashboard = (data: GetReservationDashboardRequest) =>
  useQuery<GetReservationDashboardResponse, AxiosError>({
    enabled: !!data.buildingId,
    placeholderData: keepPreviousData,
    queryFn: () => getReservationDashboard(data),
    queryKey: getReservationDashboardQueryKey(data),
  })

export const useInvalidateReservationDashboard = () => {
  const queryClient = useQueryClient()
  return () =>
    queryClient.invalidateQueries({ queryKey: [USE_GET_RESERVATION_DASHBOARD_QUERY_KEY] })
}
